import { usePerfChartSymbols } from '@/composables/usePerfChartSymbols';
import { isValidChartRecid } from '@/composables/isValidChartRecid';
import { extractStyleCacheId } from '@/utils/extractStyleCacheId';
import { CHART_TYPE_OPTION_PERF_CHART } from '@/enums/chart-type-options';

const { VITE_APP_PROTOCOL, VITE_APP_ROOT_DOMAIN, VITE_APP_PUBLIC_PATH } = import.meta.env;
const BASE_URL = `${VITE_APP_PROTOCOL}://${VITE_APP_ROOT_DOMAIN}${VITE_APP_PUBLIC_PATH}`;

async function getLinkableVersionLong(styleCacheId: string, symbol: string, annotationId: number, chartType: string, perfChartSymbolsString: string, useMainChartSymbolAsBaseline: boolean): Promise<string> {
  const pCode = await getPermanentId(styleCacheId);

  return generateLongUrl(symbol, pCode, annotationId, chartType, perfChartSymbolsString, useMainChartSymbolAsBaseline);
}

function generateLongUrl(symbol: string, styleCacheId: string, annotationId: number, chartType: string, perfChartSymbolsString: string, useMainChartSymbolAsBaseline: boolean) {
  const { formatPerfChartSymbol } = usePerfChartSymbols();

  const urlSearchParams = new URLSearchParams({
    s: symbol,
    id: styleCacheId
  });

  if (chartType === CHART_TYPE_OPTION_PERF_CHART.value) {
    urlSearchParams.set('compare', formatPerfChartSymbol(perfChartSymbolsString));
    urlSearchParams.set('perf', `${useMainChartSymbolAsBaseline}`);
  }

  if (isValidChartRecid(annotationId)) {
    urlSearchParams.set('a', annotationId.toString());
  }

  return `${BASE_URL}?${urlSearchParams.toString()}`;
}

async function getPermanentId(styleCacheId: string): Promise<string> {
  const formattedStyleCacheId = extractStyleCacheId(styleCacheId);

  if (!formattedStyleCacheId) {
    throw new Error(`Invalid styleCacheId argument provided: ${styleCacheId}.`);
  }

  if (formattedStyleCacheId.startsWith('p')) {
    return formattedStyleCacheId;
  }

  const urlSearchParams = new URLSearchParams({
    id: styleCacheId,
    chartcode: '-99'
  });

  try {
    const response = await fetch(`/stylecache/cache?${urlSearchParams.toString()}`, {
      method: 'POST'
    });

    const responseText = await response.text();

    if (!responseText.trim()) {
      throw new Error(`trimmed responseText is an empty string.`);
    }

    return responseText.trim();
  } catch (e) {
    throw new Error(`Error occurred getting permanent id for styleCacheId: ${styleCacheId}.`, { cause: e });
  }
}

async function getShortUrl(longUrl: string): Promise<string> {
  try {
    const response = await fetch('/url-shortener/api/v1/shortURL', {
      method: 'POST',
      body: JSON.stringify({
        longURL: longUrl
      }),
      headers: {
        'Content-Type': 'application/json'
      }
    });

    const responseJson = await response.json();

    if (!responseJson) {
      throw new Error(`responseJson is falsy: ${typeof responseJson} ${responseJson}.`);
    }

    if (typeof responseJson !== 'object') {
      throw new Error(`responseJson is truthy but is not an object: ${typeof responseJson}.`);
    }

    if (!('shortURL' in responseJson)) {
      throw new Error('responseJson object does not have a \'shortURL\' property.');
    }

    if (typeof responseJson.shortURL !== 'string') {
      throw new Error(`responseJson.shortURL is not a string: ${typeof responseJson.shortURL} ${responseJson.shortURL}.`);
    }

    if (!responseJson.shortURL.trim()) {
      throw new Error('Trimmed responseJson.shortURL is an empty string.');
    }

    return responseJson.shortURL.trim();
  } catch (e) {
    throw new Error(`Error occurred getting short url for long url: ${longUrl}.`, { cause: e });
  }
}

export function usePermalink() {
  return {
    getLinkableVersionLong,
    getShortUrl
  };
}
